import React, { useMemo } from "react";
import { Star, Quote, BadgeCheck } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { imgUrl } from "../../lib/api";
import { Container, Reveal } from "../site/Reveal";
import { STATES } from "../../lib/states";

// Real reviews come from Admin > Website Content > Reviews. Tag a review with a state to feature it on that state's pages.
export const visibleReviews = (content) => (content?.testimonials || []).filter((t) => t.text && t.name && (t.verified || content?.show_older_reviews));

export const useReviews = (stateName) => {
  const { content } = useSite();
  return useMemo(() => {
    const all = visibleReviews(content);
    const abbr = STATES.find((x) => x.name === stateName)?.abbr;
    const isLocal = (t) => t.state === stateName || (abbr && new RegExp(`,\\s*${abbr}\\s*$`, "i").test(t.location || ""));
    const local = stateName ? all.filter(isLocal) : [];
    const list = [...local, ...all.filter((t) => !local.includes(t))];
    const avg = all.length ? all.reduce((n, t) => n + (Number(t.rating) || 5), 0) / all.length : 0;
    return { list, local: local.length, count: all.length, avg };
  }, [content, stateName]);
};

export const Stars = ({ value = 5, size = 16 }) => (
  <span className="inline-flex gap-0.5 text-[#f59e0b]" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} fill={i <= Math.round(value) ? "currentColor" : "none"} />
    ))}
  </span>
);

export const RatingBadge = ({ stateName }) => {
  const { count, avg } = useReviews(stateName);
  if (!count) return null;
  return (
    <a href="#local-reviews" className="inline-flex items-center gap-2.5 rounded-full border border-[#f3e2bb] bg-[#fffaf0] px-4 py-2 text-[14px] font-semibold text-[#7a4f05] hover:bg-[#fff3dc] transition-colors" data-testid="rating-badge">
      <Stars value={avg} size={15} />
      <span>{avg.toFixed(1)} / 5</span>
      <span className="font-medium text-[#9a7a3f]">· {count} customer review{count !== 1 ? "s" : ""}</span>
    </a>
  );
};

const LocalReviews = ({ stateName, place }) => {
  const { list, local, count, avg } = useReviews(stateName);
  if (!count) return null;
  return (
    <section id="local-reviews" className="scroll-mt-28 py-16" data-testid="local-reviews">
      <Container>
        <Reveal className="flex flex-col items-start justify-between gap-5 rounded-[26px] bg-[#0f2e2a] p-7 text-white md:flex-row md:items-center md:p-9">
          <div>
            <div className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[#7ce3d2]">Trusted by sellers {local ? `in ${stateName}` : "across the USA"}</div>
            <h2 className="mt-2 font-jakarta text-[28px] font-bold md:text-[34px]">Why {place} sellers choose StripWise</h2>
          </div>
          <div className="flex items-center gap-4 rounded-2xl bg-white/10 px-5 py-4">
            <span className="font-jakarta text-[42px] font-extrabold leading-none">{avg.toFixed(1)}</span>
            <div>
              <Stars value={avg} size={18} />
              <div className="mt-1 text-[13px] text-white/75">Average of {count} customer review{count !== 1 ? "s" : ""}</div>
            </div>
          </div>
        </Reveal>
        <div className="mt-6 grid gap-5 md:grid-cols-3">
          {list.slice(0, 3).map((t, i) => (
            <Reveal key={`${t.name}-${i}`} delay={i * 100} className="sw-card relative flex flex-col p-6">
              <Quote size={30} className="absolute right-5 top-5 text-[#d5f3ed]" />
              <Stars value={Number(t.rating) || 5} size={15} />
              <p className="mt-3 flex-1 text-[15.5px] leading-relaxed text-[#2f4441]">“{t.text}”</p>
              <div className="mt-5 flex items-center gap-3">
                {t.image && <img src={imgUrl(t.image)} alt={t.name} className="h-11 w-11 rounded-full object-cover ring-2 ring-[#d5f3ed]" loading="lazy" />}
                <div>
                  <div className="flex items-center gap-1.5 font-semibold text-[#0f2e2a]">{t.name}<BadgeCheck size={15} className="text-[#0e8c7f]" /></div>
                  <div className="text-[13px] text-[#6b7c79]">{t.location}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default LocalReviews;
